import type { AdminAuditLogListItemResponse } from "../../../types/adminTypes";
import { formatDate } from "../../../utils/DateUtils";
import AdminPillBadge from "../ui/AdminPillBadge";
import AdminAuditFilters from "./AdminAuditFilters";
import AdminAuditTableRow from "./AdminAuditTableRow";
import AdminTableShell from "../ui/AdminTableShell";
import { adminAuditColumns } from "./adminAuditColumns";

type AdminAuditTableProps = {
  entries: AdminAuditLogListItemResponse[];
  isLoading: boolean;
  search: string;
  admin: string;
  pageSize: number;
  page: number;
  resultsCount: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onSearchChange: (value: string) => void;
  onAdminChange: (value: string) => void;
  onPageSizeChange: (value: number) => void;
  onPageChange: (nextPage: number) => void;
};

const AdminAuditTable = ({
  entries,
  isLoading,
  search,
  admin,
  pageSize,
  page,
  resultsCount,
  hasNextPage,
  hasPreviousPage,
  onSearchChange,
  onAdminChange,
  onPageSizeChange,
  onPageChange,
}: AdminAuditTableProps) => {
  return (
    <AdminTableShell
      columns={adminAuditColumns}
      filters={
        <AdminAuditFilters
          search={search}
          admin={admin}
          pageSize={pageSize}
          onSearchChange={onSearchChange}
          onAdminChange={onAdminChange}
          onPageSizeChange={onPageSizeChange}
        />
      }
      mobileCards={entries.map((entry) => (
        <article key={entry.id} className="rounded-[1.5rem] border border-myteengig-border bg-white p-4 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-semibold text-myteengig-text-primary">{entry.adminUserEmail ?? "Unknown admin"}</p>
              <p className="mt-1 text-xs text-myteengig-text-muted">{entry.adminUserId ?? "No id"}</p>
            </div>
            <AdminPillBadge>{entry.actionType}</AdminPillBadge>
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Target</p>
              <p className="mt-1 text-sm font-medium text-myteengig-text-primary">{entry.targetType}</p>
              <p className="mt-1 text-sm text-myteengig-text-muted">{entry.targetId ?? "No target id"}</p>
            </div>
            <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Created</p>
              <p className="mt-1 text-sm text-myteengig-text-primary">{formatDate(entry.createdAt)}</p>
            </div>
            <div className="rounded-2xl bg-myteengig-background/70 px-4 py-3 sm:col-span-2">
              <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Reason</p>
              <p className="mt-1 text-sm text-myteengig-text-primary">{entry.reason ?? "No reason recorded"}</p>
            </div>
          </div>
        </article>
      ))}
      rows={entries.map((entry) => (
        <AdminAuditTableRow key={entry.id} entry={entry} />
      ))}
      isLoading={isLoading}
      isEmpty={!isLoading && entries.length === 0}
      emptyMessage="No audit entries found for the current filters."
      page={page}
      pageSize={pageSize}
      resultsCount={resultsCount}
      hasNextPage={hasNextPage}
      hasPreviousPage={hasPreviousPage}
      onPageChange={onPageChange}
    />
  );
};

export default AdminAuditTable;
